import { CalendarDays, Info, PawPrint, Pill, RefreshCcw } from 'lucide-react'
import { useState } from 'react'

import { formatInstantDate, formatQuantity, pluralizeUnit } from '@snootsvet/shared'
import type { ClinicId, PublicRefillOption, RegionId } from '@snootsvet/shared/portal'
import { resolveDateFormat } from '@snootsvet/shared/portal'

import { ClinicSelect } from '@/customer/components/clinic-select'
import { FormDescription, FormLabel } from '@/customer/components/form/primitives'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { Alert, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/customer/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemContent,
  SelectItemDescription,
  SelectItemLabel,
  SelectTrigger,
  SelectValue,
} from '@/customer/components/ui/select'
import { useCreateOrderRefill } from '@/customer/hooks/use-create-order-refill'
import { useListOrderRefills } from '@/customer/hooks/use-list-order-refills'
import { useRegion } from '@/customer/hooks/use-region'
import { useRegionClinics } from '@/customer/hooks/use-region-clinics'

/** Dialog for creating one pending order from an eligible prescription refill. */
export function RefillOrderDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const region = useRegion()
  const clinics = useRegionClinics()
  const { data, isPending } = useListOrderRefills(open)
  const createRefill = useCreateOrderRefill()
  const [treatmentId, setTreatmentId] = useState<string | null>(null)
  const [clinicId, setClinicId] = useState<string | null>(null)
  const refills = data?.refills ?? []
  const selected = refills.find((refill) => refill.treatmentId === treatmentId) ?? null
  const stockedClinics = clinics.filter((clinic) =>
    selected?.stock.some((stock) => stock.clinicId === clinic.id)
  )
  const selectedClinicStock = selected?.stock.find((stock) => stock.clinicId === clinicId)

  function selectMedication(id: string | null) {
    const refill = refills.find((option) => option.treatmentId === id)
    if (refill && !refill.isEligibleForRefill) return
    setTreatmentId(id)
    const suggestedClinic = clinics.find(
      (clinic) =>
        clinic.id === refill?.suggestedClinicId &&
        refill?.stock.some((stock) => stock.clinicId === clinic.id)
    )
    setClinicId(suggestedClinic?.id ?? null)
  }

  async function confirm() {
    if (
      !selected?.isEligibleForRefill ||
      !treatmentId ||
      !stockedClinics.some((clinic) => clinic.id === clinicId)
    )
      return
    await createRefill.mutateAsync({ treatmentId, clinicId: clinicId as ClinicId })
    onOpenChange(false)
    setTreatmentId(null)
    setClinicId(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader tone="yellow">
          <DialogTitle>Re-order medication</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            Request the next instalment for an eligible prescription and choose where to collect it.
            We&apos;ll notify you when your order is ready.
          </DialogDescription>
          {isPending ? (
            <LoadingSpinner />
          ) : refills.length === 0 ? (
            <p className="text-body-sm">You have no prescribed medications eligible for refill.</p>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <FormLabel htmlFor="refill-medication" size="lg" className="mb-2">
                  Which medication would you like to re-order?
                </FormLabel>
                <Select
                  value={treatmentId}
                  onValueChange={selectMedication}
                  items={refills.map((r) => ({
                    value: r.treatmentId,
                    label: `${r.productName} for ${r.petName}`,
                  }))}
                >
                  <SelectTrigger id="refill-medication" className="h-auto min-h-16">
                    <SelectValue placeholder="Select medication">
                      {(label) =>
                        selected ? (
                          <RefillOptionRow refill={selected} regionId={region.id} />
                        ) : (
                          label
                        )
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {refills.map((refill) => (
                      <SelectItem
                        key={refill.treatmentId}
                        value={refill.treatmentId}
                        disabled={!refill.isEligibleForRefill}
                      >
                        <RefillOptionRow refill={refill} regionId={region.id} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription className="mt-2">
                  Only medications prescribed by a vet with refills remaining are eligible for
                  self-service.
                </FormDescription>
              </div>
              {selected?.isEligibleForRefill && (
                <div className="flex flex-col gap-3">
                  <FormLabel htmlFor="refill-clinic" size="lg">
                    Which clinic would you like to collect from?
                  </FormLabel>
                  <ClinicSelect
                    id="refill-clinic"
                    value={clinicId}
                    onValueChange={setClinicId}
                    clinics={stockedClinics}
                    accessory={(clinic) => {
                      const status = selected?.stock.find(
                        (stock) => stock.clinicId === clinic.id
                      )?.status
                      return status ? (
                        <span
                          className={
                            status === 'healthy'
                              ? 'text-label-sm text-green'
                              : 'text-label-sm text-warning'
                          }
                        >
                          {status === 'healthy' ? 'In stock' : 'Out of stock'}
                        </span>
                      ) : null
                    }}
                  />
                  {selectedClinicStock?.status === 'out_of_stock' && (
                    <Alert variant="info" className="mt-1">
                      <Info />
                      <AlertTitle>Back soon</AlertTitle>
                      <AlertDescription>
                        This medication is temporarily out of stock at the selected clinic. There
                        may be a delay in preparing your order.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {refills.length > 0 && (
            <Button
              disabled={
                !selected?.isEligibleForRefill ||
                !stockedClinics.some((clinic) => clinic.id === clinicId) ||
                createRefill.isPending
              }
              onClick={confirm}
            >
              {createRefill.isPending ? 'Ordering…' : 'Confirm order'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RefillOptionRow({ refill, regionId }: { refill: PublicRefillOption; regionId: RegionId }) {
  const cooldownDays = refill.refillCooldownEndsAt
    ? Math.max(0, Math.ceil((Date.parse(refill.refillCooldownEndsAt) - Date.now()) / 86_400_000))
    : 0

  return (
    <SelectItemContent>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <SelectItemLabel className="text-button">{refill.productName}</SelectItemLabel>
        {!refill.isEligibleForRefill && refill.refillCooldownEndsAt && (
          <span className="whitespace-nowrap text-label-sm font-bold text-destructive">
            {cooldownDays > 0
              ? `Available in ${cooldownDays} ${cooldownDays === 1 ? 'day' : 'days'} · `
              : 'Available from '}
            {formatInstantDate(refill.refillCooldownEndsAt, resolveDateFormat(null, regionId))}
          </span>
        )}
      </span>
      <SelectItemDescription className="flex flex-wrap gap-x-3 gap-y-1">
        <span className="flex items-center gap-1">
          <PawPrint className="size-3.5" aria-hidden />
          {refill.petName}
        </span>
        <span className="flex items-center gap-1">
          <CalendarDays className="size-3.5" aria-hidden />
          {formatInstantDate(refill.prescribedAt, resolveDateFormat(null, regionId))}
        </span>
        <span className="flex items-center gap-1">
          <Pill className="size-3.5" aria-hidden />
          {formatQuantity(refill.quantity)} {pluralizeUnit(refill.unit, refill.quantity)}
        </span>
        <span className="flex items-center gap-1">
          <RefreshCcw className="size-3.5" aria-hidden />
          {refill.refillsRemaining} {refill.refillsRemaining === 1 ? 'refill' : 'refills'} remaining
        </span>
      </SelectItemDescription>
    </SelectItemContent>
  )
}
